
import React, { useState, useEffect } from 'react';
import { db } from '../services/firebase';
import { collection, query, where, onSnapshot, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { UserRole } from '../types';

interface Props {
    role?: UserRole;
}

const StudentManagement: React.FC<Props> = ({ role = UserRole.STUDENT }) => {
    const [users, setUsers] = useState<any[]>([]);
    const [classes, setClasses] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSyncing, setIsSyncing] = useState(true);

    useEffect(() => {
        setIsSyncing(true);
        // Create a real-time listener for users filtered by role
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('role', '==', role));
        
        const unsubscribeUsers = onSnapshot(q, (snapshot) => {
            const userData = snapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data() 
            }));
            setUsers(userData);
            setIsSyncing(false);
        }, (error) => {
            console.error("Error fetching users:", error);
            setIsSyncing(false);
        });

        // Create a real-time listener for classes to populate the dropdowns
        const classesRef = collection(db, 'classes');
        const unsubscribeClasses = onSnapshot(classesRef, (snapshot) => {
            setClasses(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        // Clean up listeners on unmount or when role changes
        return () => {
            unsubscribeUsers();
            unsubscribeClasses();
        };
    }, [role]); // Re-run effect if the role prop changes

    const handleAssignClass = async (userId: string, classId: string) => {
        try {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, { classId });
        } catch (err) {
            console.error("Update failed:", err);
            alert("Error updating class: " + err);
        }
    };

    const handlePromote = async (userId: string, targetRole: UserRole) => {
        if (confirm(`Change role to ${targetRole}?`)) {
            try {
                const userRef = doc(db, 'users', userId);
                await updateDoc(userRef, { role: targetRole });
            } catch (err) {
                console.error("Promotion failed:", err);
                alert("Error promoting user: " + err);
            }
        }
    };

    const handleDelete = async (id: string) => {
        if(confirm(`Remove this user from the portal?`)) {
            try {
                await deleteDoc(doc(db, 'users', id));
            } catch (err) {
                console.error("Delete failed:", err);
                alert("Delete failed: " + err);
            }
        }
    };

    const filtered = users.filter(u => 
        (u.displayName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-bold dark:text-white capitalize">Registered {role}s</h2>
                    {isSyncing && (
                        <div className="flex items-center gap-2 text-purple-500 animate-pulse">
                            <i className="fas fa-sync-alt fa-spin text-xs"></i>
                            <span className="text-[10px] font-black uppercase">Syncing...</span>
                        </div>
                    )}
                </div>
                <div className="text-xs font-bold text-gray-400 uppercase bg-gray-100 dark:bg-gray-800 px-4 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 shadow-sm">
                    Total: {users.length}
                </div>
            </div>

            <div className="relative group">
                <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-purple-500 transition-colors"></i>
                <input 
                    type="text" 
                    placeholder={`Search ${role}s by name or email...`} 
                    value={searchTerm} 
                    onChange={(e) => setSearchTerm(e.target.value)} 
                    className="w-full pl-12 pr-4 py-4 bg-white dark:bg-dark-card border border-gray-100 dark:border-gray-800 rounded-2xl dark:text-white outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all shadow-sm" 
                />
            </div>

            <div className="bg-white dark:bg-dark-card rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800 overflow-hidden relative">
                <div className="table-container">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50/50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
                            <tr>
                                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Identification</th>
                                {role === UserRole.STUDENT && <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Assigned Class</th>}
                                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Management</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {filtered.map(u => (
                                <tr key={u.id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-all">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-4">
                                            <div className="relative">
                                                <img src={u.photoURL} className="w-11 h-11 rounded-2xl object-cover ring-2 ring-transparent group-hover:ring-purple-500 transition-all" alt="" />
                                                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white dark:border-dark-card rounded-full"></div>
                                            </div>
                                            <div>
                                                <p className="font-black dark:text-white text-sm leading-none mb-1">{u.displayName}</p>
                                                <p className="text-[10px] text-gray-400 font-bold">{u.email}</p>
                                            </div>
                                        </div>
                                    </td>
                                    {role === UserRole.STUDENT && (
                                        <td className="px-6 py-4">
                                            <select 
                                                className="bg-gray-50 dark:bg-gray-800/50 p-2.5 rounded-xl text-xs font-bold dark:text-white outline-none border border-gray-100 dark:border-gray-700 focus:border-purple-500 min-w-[140px]"
                                                value={u.classId || ''}
                                                onChange={(e) => handleAssignClass(u.id, e.target.value)}
                                            >
                                                <option value="">-- Unassigned --</option>
                                                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                            </select>
                                        </td>
                                    )}
                                    <td className="px-6 py-4 text-right space-x-2">
                                        {role === UserRole.STUDENT ? (
                                            <button 
                                                onClick={() => handlePromote(u.id, UserRole.STAFF)}
                                                className="px-4 py-2 bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                                                title="Promote to Staff Member"
                                            >
                                                <i className="fas fa-chevron-up mr-2"></i> Promote
                                            </button>
                                        ) : (
                                            <button 
                                                onClick={() => handlePromote(u.id, UserRole.STUDENT)}
                                                className="px-4 py-2 bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-600 hover:text-white transition-all shadow-sm"
                                                title="Demote to Student"
                                            >
                                                <i className="fas fa-chevron-down mr-2"></i> Demote
                                            </button>
                                        )}
                                        <button 
                                            onClick={() => handleDelete(u.id)} 
                                            className="p-3 text-red-400 hover:text-white hover:bg-red-500 rounded-xl transition-all shadow-sm"
                                        >
                                            <i className="fas fa-trash-alt"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            
            {!isSyncing && filtered.length === 0 && (
                <div className="text-center py-32 bg-white dark:bg-dark-card rounded-[3rem] border-2 border-dashed border-gray-100 dark:border-gray-800 shadow-inner">
                    <div className="w-20 h-20 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="fas fa-users-slash text-3xl text-gray-200"></i>
                    </div>
                    <h3 className="text-xl font-bold dark:text-white">No {role}s Found</h3>
                    <p className="text-gray-400 text-sm mt-2">Try adjusting your search criteria or role filters.</p>
                </div>
            )}
        </div>
    );
};

export default StudentManagement;
